// src/pages/AbonnementsList.js - VERSION MODIFIÉE (2 CARTES PAR LIGNE)
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

// Déclarer COLORS en premier
const COLORS = {
  primary: '#0B3C5D',
  secondary: '#1E88C8',
  accent: '#4FC3F7',
  darkText: '#2E2E2E',
  lightBg: '#F5F7FA',
  white: '#FFFFFF',
  danger: '#f44336',
  success: '#4caf50',
  warning: '#ff9800',
  info: '#2196f3'
};

// Compte à rebours jusqu'à l'expiration (24h) d'un code d'achat NITA/Amana
// (même composant que sur AbonnementsDetails.js / AbonnementsCreate.js).
const CompteARebours = ({ createdAt }) => {
  const [texte, setTexte] = useState('');

  useEffect(() => {
    if (!createdAt) {
      setTexte('');
      return;
    }
    const calculer = () => {
      const limite = new Date(createdAt.replace(' ', 'T')).getTime() + 24 * 60 * 60 * 1000;
      const restantMs = limite - Date.now();
      if (restantMs <= 0) {
        setTexte('Référence expirée');
        return;
      }
      const heures = Math.floor(restantMs / (1000 * 60 * 60));
      const minutes = Math.floor((restantMs % (1000 * 60 * 60)) / (1000 * 60));
      setTexte(`Temps restant pour payer : ${heures}h ${minutes}min`);
    };
    calculer();
    const interval = setInterval(calculer, 60000);
    return () => clearInterval(interval);
  }, [createdAt]);

  if (!texte) return null;
  return <span>{texte}</span>;
};

// Options de filtres
const STATUT_OPTIONS = [
  { value: 'all', label: 'Tous les statuts' },
  { value: 'actif', label: 'Actifs' },
  { value: 'expire', label: 'Expirés' },
  { value: 'inactif', label: 'Inactifs' }
];

// Options de tri
const ORDER_OPTIONS = [
  { value: 'recent', label: 'Plus récent' },
  { value: 'ancien', label: 'Plus ancien' },
  { value: 'prix_asc', label: 'Prix (croissant)' },
  { value: 'prix_desc', label: 'Prix (décroissant)' },
  { value: 'expiration', label: 'Expiration proche' }
];

const AbonnementsList = () => {
  const navigate = useNavigate();
  const [abonnements, setAbonnements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [serverError, setServerError] = useState("");
  const [infoMsg, setInfoMsg] = useState("");
  const [currentProfile, setCurrentProfile] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [search, setSearch] = useState("");
  const [filterStatut, setFilterStatut] = useState("all");
  const [filterSociete, setFilterSociete] = useState("");
  const [orderBy, setOrderBy] = useState("recent");
  const [societes, setSocietes] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    actifs: 0,
    expires: 0,
    inactifs: 0,
    valeurTotale: 0
  });

  useEffect(() => {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        setCurrentProfile(String(user.profile));
        setCurrentUser(user);
      } catch (e) {
        console.error("Erreur parsing user:", e);
      }
    }

    if (currentProfile === "1") {
      fetchSocietes();
    }
  }, [currentProfile]);

  // Formater le prix en FCFA
  const formatPrix = (montant) => {
    const montantArrondi = Math.round(parseFloat(montant) || 0);
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(montantArrondi);
  };

  // Formater la date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Calculer les jours restants
  const calculateJoursRestants = (dateFin) => {
    const fin = new Date(dateFin);
    const maintenant = new Date();
    const diffTime = fin - maintenant;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Obtenir le badge de statut
  const getStatusBadge = (abonnement) => {
    const joursRestants = calculateJoursRestants(abonnement.date_fin_abonnement);
    const dateFin = new Date(abonnement.date_fin_abonnement);
    const maintenant = new Date();

    if (abonnement.statut_abonnement === 0) {
      // Créé en attente d'un paiement en ligne (myNITA/Amana) jamais finalisé —
      // à distinguer d'un abonnement simplement désactivé, pour que
      // l'utilisateur comprenne qu'il peut reprendre ou abandonner ce paiement.
      if (abonnement.mode_paiement === 'en_ligne' && abonnement.statut_paiement === 'pending') {
        return {
          label: 'Paiement non finalisé',
          color: 'warning',
          icon: 'bi-hourglass-split',
          bgColor: '#FFF3E0',
          textColor: '#EF6C00',
          cardHeaderColor: '#ff9800'
        };
      }
      return {
        label: 'Inactif',
        color: 'secondary',
        icon: 'bi-pause-circle',
        bgColor: '#E0E0E0',
        textColor: '#757575',
        cardHeaderColor: '#B0BEC5'
      };
    } else if (abonnement.statut_abonnement === 1) {
      if (dateFin < maintenant) {
        return { 
          label: 'Expiré', 
          color: 'danger', 
          icon: 'bi-exclamation-circle', 
          bgColor: '#FFEBEE', 
          textColor: '#C62828',
          cardHeaderColor: '#f44336'
        };
      } else if (joursRestants <= 7) {
        return { 
          label: `Expire dans ${joursRestants}j`, 
          color: 'warning', 
          icon: 'bi-clock', 
          bgColor: '#FFF3E0', 
          textColor: '#EF6C00',
          cardHeaderColor: '#ff9800'
        };
      } else {
        return { 
          label: 'Actif', 
          color: 'success', 
          icon: 'bi-check-circle', 
          bgColor: '#E8F5E9', 
          textColor: '#2E7D32',
          cardHeaderColor: '#4caf50'
        };
      }
    }
    
    return { 
      label: 'Inconnu', 
      color: 'dark', 
      icon: 'bi-question-circle', 
      bgColor: '#F5F5F5', 
      textColor: '#424242',
      cardHeaderColor: '#757575'
    };
  };

  // Obtenir l'icône du type d'abonnement
  const getTypeIcon = (type) => {
    switch(type) {
      case 1: return 'bi-calendar-month';
      case 2: return 'bi-calendar3';
      case 3: return 'bi-calendar2-range';
      case 4: return 'bi-calendar-year';
      default: return 'bi-calendar';
    }
  };

  // Obtenir le libellé du type
  const getTypeLabel = (type) => {
    switch(type) {
      case 1: return 'Mensuel';
      case 2: return 'Trimestriel';
      case 3: return 'Semestriel';
      case 4: return 'Annuel';
      default: return 'Personnalisé';
    }
  };

  // Obtenir la couleur du type
  const getTypeColor = (type) => {
    switch(type) {
      case 1: return COLORS.info;
      case 2: return COLORS.secondary;
      case 3: return COLORS.accent;
      case 4: return COLORS.primary;
      default: return COLORS.darkText;
    }
  };

  // Obtenir l'email tronqué
  const getSafeEmail = (email) => {
    if (!email) return "Non spécifié";
    const parts = email.split('@');
    if (parts.length === 2) {
      const username = parts[0];
      const domain = parts[1];
      if (username.length <= 4) {
        return `***@${domain}`;
      }
      return `${username.substring(0, 3)}***@${domain}`;
    }
    return "***@***";
  };

  // Calculer le prix mensuel
  const calculerPrixMensuel = (montant, type) => {
    const montantNum = parseFloat(montant) || 0;
    switch(type) {
      case 1: return montantNum; // Mensuel
      case 2: return montantNum / 3; // Trimestriel
      case 3: return montantNum / 6; // Semestriel
      case 4: return montantNum / 12; // Annuel
      default: return montantNum;
    }
  };

  // Formater la durée
  const formatDuree = (type) => {
    switch(type) {
      case 1: return '1 mois';
      case 2: return '3 mois';
      case 3: return '6 mois';
      case 4: return '1 an';
      default: return 'Personnalisé';
    }
  };

  // Récupérer la liste des sociétés
  const fetchSocietes = useCallback(async () => {
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.get(`${API_BASE_URL}/societes/list.php`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.data?.success) {
        setSocietes(response.data.societes || []);
      }
    } catch (error) {
      console.error("Erreur chargement sociétés:", error);
    }
  }, []);

  // Charger les abonnements
  const fetchAbonnements = useCallback(async () => {
    setLoading(true);
    setServerError("");

    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setServerError("Session expirée. Veuillez vous reconnecter.");
        setLoading(false);
        return;
      }

      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (filterStatut !== "all") params.append("statut", filterStatut);
      if (orderBy) params.append("order_by", orderBy);
      
      if (currentProfile === "1" && filterSociete) {
        params.append("societe_id", filterSociete);
      }
      
      if (currentProfile === "3" && currentUser?.societe_id) {
        params.append("societe_id", currentUser.societe_id);
      }

      const url = `${API_BASE_URL}/abonnements/list.php?${params.toString()}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.data?.success) {
        const abonnementsData = response.data.abonnements || [];
        setAbonnements(abonnementsData);
        calculateStats(abonnementsData);
        
        if (abonnementsData.length === 0) {
          setInfoMsg("Aucun abonnement trouvé avec les critères sélectionnés.");
        } else {
          setInfoMsg("");
        }
      } else {
        setServerError(response.data?.message || "Erreur lors du chargement des abonnements.");
      }
    } catch (error) {
      console.error("Erreur chargement abonnements:", error);
      
      if (error.response?.status === 401) {
        setServerError("Session expirée. Veuillez vous reconnecter.");
        localStorage.clear();
        setTimeout(() => navigate("/login"), 2000);
      } else if (error.response?.status === 403) {
        setServerError("Accès interdit. Permissions insuffisantes.");
      } else {
        setServerError("Erreur lors du chargement des abonnements.");
      }
    } finally {
      setLoading(false);
    }
  }, [search, filterStatut, filterSociete, orderBy, currentProfile, currentUser, navigate]);

  useEffect(() => {
    fetchAbonnements();
  }, [fetchAbonnements]);

  // Calculer les statistiques
  const calculateStats = (abonnementsList) => {
    const now = new Date();
    
    const statsData = {
      total: abonnementsList.length,
      actifs: 0,
      expires: 0,
      inactifs: 0,
      valeurTotale: 0
    };

    abonnementsList.forEach(abonnement => {
      const dateFin = new Date(abonnement.date_fin_abonnement);
      // Un paiement en ligne jamais finalisé n'a rien encaissé : l'exclure de
      // la valeur totale évite de compter un revenu qui n'existe pas.
      const paiementNonFinalise = abonnement.mode_paiement === 'en_ligne' && abonnement.statut_paiement === 'pending';
      if (!paiementNonFinalise) {
        statsData.valeurTotale += parseFloat(abonnement.montant_abonnement) || 0;
      }

      if (abonnement.statut_abonnement === 1) {
        if (dateFin >= now) {
          statsData.actifs++;
        } else {
          statsData.expires++;
        }
      } else if (abonnement.statut_abonnement === 0) {
        statsData.inactifs++;
      }
    });

    setStats(statsData);
  };

  // Gérer la recherche
  const handleSearch = (e) => {
    e.preventDefault();
    fetchAbonnements();
  };

  // Effacer les filtres
  const clearFilters = () => {
    setSearch("");
    setFilterStatut("all");
    setFilterSociete("");
    setOrderBy("recent");
    fetchAbonnements();
  };

  // Vérifier si l'utilisateur peut créer un abonnement
  const canCreateAbonnement = () => {
    return currentProfile === "1" || currentProfile === "3";
  };

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Abonnements</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard">Tableau de bord</Link>
            </li>
            <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Abonnements</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="card" style={{ 
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          border: 'none'
        }}>
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h5 className="card-title mb-1" style={{ 
                  color: COLORS.primary,
                  borderBottom: `2px solid ${COLORS.lightBg}`,
                  paddingBottom: '16px',
                  width: '100%'
                }}>
                  <div className="d-flex align-items-center">
                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-3" style={{ 
                      width: '36px',
                      height: '36px',
                      backgroundColor: '#E3F2FD',
                      color: COLORS.primary
                    }}>
                      <i className="bi bi-calendar-check"></i>
                    </div>
                    Liste des abonnements
                  </div>
                </h5>
                <p className="text-muted mb-0" style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                  <i className="bi bi-info-circle me-1"></i>
                  {currentProfile === "3" ? "Vos abonnements" : "Gestion des abonnements des sociétés"}
                </p>
              </div>
              
              {/* Boutons d'action avec le nouveau bouton Achats de quotas */}
              <div className="d-flex gap-2">
                <button
                  className="btn fw-semibold"
                  onClick={fetchAbonnements}
                  disabled={loading}
                  style={{
                    color: COLORS.primary,
                    borderColor: COLORS.primary,
                    backgroundColor: 'transparent',
                    borderRadius: '8px',
                    padding: '10px 20px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = COLORS.primary;
                    e.target.style.color = COLORS.white;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = COLORS.primary;
                  }}
                >
                  <i className={`bi ${loading ? 'bi-arrow-clockwise spin' : 'bi-arrow-clockwise'} me-2`}></i>
                  {loading ? 'Actualisation...' : 'Actualiser'}
                </button>
                
                {/* NOUVEAU BOUTON - Achats de quotas */}
                <Link
                  to="/quotas/list"
                  className="btn fw-semibold"
                  style={{
                    color: COLORS.white,
                    backgroundColor: COLORS.info,
                    borderColor: COLORS.info,
                    borderRadius: '8px',
                    padding: '10px 20px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = COLORS.secondary;
                    e.target.style.borderColor = COLORS.secondary;
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 12px rgba(30, 136, 200, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = COLORS.info;
                    e.target.style.borderColor = COLORS.info;
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <i className="bi bi-cart-check me-2"></i>
                  Achats de quotas
                </Link>
                
                {canCreateAbonnement() && (
                  <Link
                    to="/abonnements/create"
                    className="btn fw-semibold"
                    style={{
                      color: COLORS.white,
                      backgroundColor: COLORS.primary,
                      borderColor: COLORS.primary,
                      borderRadius: '8px',
                      padding: '10px 24px',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = COLORS.secondary;
                      e.target.style.borderColor = COLORS.secondary;
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 6px 12px rgba(11, 60, 93, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = COLORS.primary;
                      e.target.style.borderColor = COLORS.primary;
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    <i className="bi bi-plus-lg me-2"></i>
                    {currentProfile === "1" ? "Nouvel abonnement" : "Souscrire"}
                  </Link>
                )}
              </div>
            </div>

            {/* Filtres */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="card" style={{ 
                  backgroundColor: '#E3F2FD',
                  border: 'none',
                  borderRadius: '8px'
                }}>
                  <div className="card-body py-3">
                    <form onSubmit={handleSearch} className="row g-3">
                      {/* Ligne 1 : Recherche, Statut, Société */}
                      <div className="col-md-4">
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-search"></i>
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Rechercher un abonnement..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            style={{
                              borderColor: '#e0e0e0',
                              color: COLORS.darkText
                            }}
                          />
                        </div>
                      </div>
                      
                      <div className="col-md-3">
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-filter"></i>
                          </span>
                          <select
                            className="form-select"
                            value={filterStatut}
                            onChange={(e) => setFilterStatut(e.target.value)}
                            style={{
                              borderColor: '#e0e0e0',
                              color: COLORS.darkText
                            }}
                          >
                            {STATUT_OPTIONS.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {currentProfile === "1" && (
                        <div className="col-md-3">
                          <div className="input-group">
                            <span className="input-group-text" style={{ 
                              backgroundColor: COLORS.lightBg,
                              borderColor: '#e0e0e0',
                              color: COLORS.secondary
                            }}>
                              <i className="bi bi-building"></i>
                            </span>
                            <select
                              className="form-select"
                              value={filterSociete}
                              onChange={(e) => setFilterSociete(e.target.value)}
                              style={{
                                borderColor: '#e0e0e0',
                                color: COLORS.darkText
                              }}
                            >
                              <option value="">Toutes les sociétés</option>
                              {societes.map(societe => (
                                <option key={societe.id_societe} value={societe.id_societe}>
                                  {societe.nom_societe}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      )}
                      
                      <div className="col-md-2">
                        <div className="d-flex gap-2">
                          <button 
                            type="submit" 
                            className="btn fw-semibold"
                            style={{
                              backgroundColor: COLORS.primary,
                              borderColor: COLORS.primary,
                              color: COLORS.white,
                              borderRadius: '8px',
                              padding: '10px 20px',
                              flex: 1,
                              transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = COLORS.secondary;
                              e.target.style.borderColor = COLORS.secondary;
                              e.target.style.transform = 'translateY(-2px)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = COLORS.primary;
                              e.target.style.borderColor = COLORS.primary;
                              e.target.style.transform = 'translateY(0)';
                            }}
                          >
                            <i className="bi bi-search me-2"></i>
                            Appliquer
                          </button>
                        </div>
                      </div>
                    </form>
                    
                    {/* Ligne 2 : Tri et Bouton Effacer */}
                    <div className="row mt-3">
                      <div className="col-md-4">
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-sort-down"></i>
                          </span>
                          <select
                            className="form-select"
                            value={orderBy}
                            onChange={(e) => setOrderBy(e.target.value)}
                            style={{
                              borderColor: '#e0e0e0',
                              color: COLORS.darkText
                            }}
                          >
                            {ORDER_OPTIONS.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      <div className="col-md-4"></div>
                      
                      <div className="col-md-4">
                        <button
                          type="button"
                          className="btn fw-semibold mt-md-0"
                          onClick={clearFilters}
                          style={{
                            color: COLORS.primary,
                            borderColor: COLORS.primary,
                            backgroundColor: 'transparent',
                            borderRadius: '8px',
                            padding: '10px 24px',
                            transition: 'all 0.3s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = COLORS.primary;
                            e.target.style.color = COLORS.white;
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                            e.target.style.color = COLORS.primary;
                          }}
                        >
                          <i className="bi bi-x-lg me-2"></i>
                          Effacer les filtres
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {serverError && (
              <div className="alert alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#ffebee',
                borderColor: COLORS.danger,
                color: '#c62828',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.danger}`
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <div className="flex-grow-1">{serverError}</div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setServerError("")}
                  ></button>
                </div>
              </div>
            )}
            
            {infoMsg && (
              <div className="alert alert-info alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#e3f2fd',
                borderColor: COLORS.secondary,
                color: COLORS.primary,
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.secondary}`
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-info-circle me-2"></i>
                  <div className="flex-grow-1">{infoMsg}</div>
                  <button type="button" className="btn-close" onClick={() => setInfoMsg("")}></button>
                </div>
              </div>
            )}

            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border" role="status" style={{ color: COLORS.primary }}>
                  <span className="visually-hidden">Chargement...</span>
                </div>
                <p className="mt-2" style={{ color: COLORS.darkText }}>Chargement des abonnements...</p>
              </div>
            ) : (
              <>
                {abonnements.length === 0 ? (
                  <div className="text-center py-5">
                    <div className="mb-4">
                      <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ 
                        width: '80px',
                        height: '80px',
                        backgroundColor: '#F5F7FA',
                        color: '#B0BEC5'
                      }}>
                        <i className="bi bi-calendar-check fs-1"></i>
                      </div>
                    </div>
                    <h6 className="mb-3" style={{ color: COLORS.primary }}>Aucun abonnement trouvé</h6>
                    <p className="text-muted" style={{ color: COLORS.darkText, maxWidth: '500px', margin: '0 auto' }}>
                      {search || filterStatut !== 'all' || filterSociete
                        ? "Aucun abonnement ne correspond à vos critères." 
                        : "Aucun abonnement n'a été trouvé."}
                    </p>
                    {(search || filterStatut !== 'all' || filterSociete) && (
                      <button
                        className="btn fw-semibold mt-3"
                        onClick={clearFilters}
                        style={{
                          color: COLORS.primary,
                          borderColor: COLORS.primary,
                          backgroundColor: 'transparent',
                          borderRadius: '8px',
                          padding: '8px 20px',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#E3F2FD';
                          e.target.style.borderColor = COLORS.secondary;
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.borderColor = COLORS.primary;
                        }}
                      >
                        <i className="bi bi-x-lg me-2"></i>
                        Effacer les filtres
                      </button>
                    )}
                  </div>
                ) : (
                  <>
                    {/* Statistiques */}
                    <div className="row mb-4">
                      <div className="col-md-3 mb-3">
                        <div className="card" style={{ 
                          backgroundColor: '#E3F2FD',
                          border: 'none',
                          borderRadius: '8px'
                        }}>
                          <div className="card-body text-center py-3">
                            <div className="fw-bold" style={{ 
                              color: COLORS.primary,
                              fontSize: '1.5rem'
                            }}>
                              {stats.total}
                            </div>
                            <small className="text-muted">Total abonnements</small>
                          </div>
                        </div>
                      </div>
                      
                      <div className="col-md-3 mb-3">
                        <div className="card" style={{ 
                          backgroundColor: '#E8F5E9',
                          border: 'none',
                          borderRadius: '8px'
                        }}>
                          <div className="card-body text-center py-3">
                            <div className="fw-bold" style={{ 
                              color: COLORS.success,
                              fontSize: '1.5rem'
                            }}>
                              {stats.actifs}
                            </div>
                            <small className="text-muted">Actifs</small>
                          </div>
                        </div>
                      </div>
                      
                      <div className="col-md-3 mb-3">
                        <div className="card" style={{ 
                          backgroundColor: '#FFF3E0',
                          border: 'none',
                          borderRadius: '8px'
                        }}>
                          <div className="card-body text-center py-3">
                            <div className="fw-bold" style={{ 
                              color: COLORS.warning,
                              fontSize: '1.5rem'
                            }}>
                              {stats.expires}
                            </div>
                            <small className="text-muted">Expirés</small>
                          </div>
                        </div>
                      </div>
                      
                      <div className="col-md-3 mb-3">
                        <div className="card" style={{ 
                          backgroundColor: '#F5F5F5',
                          border: 'none',
                          borderRadius: '8px'
                        }}>
                          <div className="card-body text-center py-3">
                            <div className="fw-bold" style={{ 
                              color: COLORS.darkText,
                              fontSize: '1.5rem'
                            }}>
                              {formatPrix(stats.valeurTotale)}
                            </div>
                            <small className="text-muted">Valeur totale</small>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Liste des abonnements en cartes - MODIFIÉ POUR 2 CARTES PAR LIGNE */}
                    <div className="row g-4">
                      {abonnements.map((abonnement) => {
                        const statusBadge = getStatusBadge(abonnement);
                        const joursRestants = calculateJoursRestants(abonnement.date_fin_abonnement);
                        const typeColor = getTypeColor(abonnement.type_abonnment);
                        const prixMensuel = calculerPrixMensuel(abonnement.montant_abonnement, abonnement.type_abonnment);
                        
                        return (
                          <div key={abonnement.id_abonnement} className="col-md-6 col-lg-6 col-xl-6">
                            {/* CARTE AGRANDIE */}
                            <div className="card h-100" style={{ 
                              border: `1px solid ${statusBadge.cardHeaderColor}30`,
                              borderRadius: '12px',
                              overflow: 'hidden',
                              transition: 'all 0.3s ease',
                              backgroundColor: COLORS.white,
                              height: '100%',
                              minHeight: '450px'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'translateY(-6px)';
                              e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.15)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = 'none';
                            }}>
                              {/* En-tête de la carte - AGRANDI */}
                              <div className="card-header border-0 pb-0" style={{ 
                                backgroundColor: statusBadge.cardHeaderColor,
                                color: COLORS.white,
                                padding: '24px 24px 16px 24px'
                              }}>
                                <div className="d-flex justify-content-between align-items-start">
                                  <div style={{ flex: 1 }}>
                                    <h5 className="card-title mb-2" style={{ 
                                      color: COLORS.white,
                                      fontSize: '1.3rem',
                                      fontWeight: '600',
                                      lineHeight: '1.4'
                                    }}>
                                      {abonnement.libele_offre || abonnement.libele_abonnement}
                                      {abonnement.mode_paiement === 'essai' && (
                                        <span className="badge ms-2" style={{
                                          backgroundColor: 'rgba(255,255,255,0.9)',
                                          color: '#6D28D9',
                                          fontSize: '0.7rem',
                                          verticalAlign: 'middle'
                                        }}>
                                          <i className="bi bi-stars me-1"></i>ESSAI
                                        </span>
                                      )}
                                    </h5>
                                    <div className="d-flex align-items-center">
                                      <i className="bi bi-building me-2 fs-5"></i>
                                      <span className="badge fw-semibold" style={{ 
                                        backgroundColor: 'rgba(255,255,255,0.25)',
                                        color: COLORS.white,
                                        fontSize: '0.85rem',
                                        padding: '6px 12px',
                                        borderRadius: '6px'
                                      }}>
                                        {abonnement.nom_societe}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Corps de la carte - AGRANDI */}
                              <div className="card-body pt-4 pb-3">
                                {/* Informations Société */}
                                <div className="mb-4">
                                  <div className="d-flex align-items-center mb-3">
                                    <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                                      width: '48px',
                                      height: '48px',
                                      backgroundColor: '#E3F2FD',
                                      color: COLORS.primary
                                    }}>
                                      <i className="bi bi-building fs-5"></i>
                                    </div>
                                    <div style={{ flex: 1 }}>
                                      <div style={{ color: COLORS.primary, fontWeight: '600', fontSize: '1.05rem' }}>
                                        {abonnement.nom_societe}
                                      </div>
                                      <div className="text-muted mt-1" style={{ fontSize: '0.85rem' }}>
                                        <i className="bi bi-envelope me-1"></i>
                                        {getSafeEmail(abonnement.email_societe)}
                                      </div>
                                      {abonnement.telephone_societe && (
                                        <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                                          <i className="bi bi-telephone me-1"></i>
                                          {abonnement.telephone_societe}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Grille d'informations - AGRANDIE */}
                                <div className="row mb-4">
                                  <div className="col-6 mb-3">
                                    <div className="text-center p-3 rounded h-100" style={{ 
                                      backgroundColor: '#E3F2FD',
                                      border: `1px solid ${COLORS.secondary}20`,
                                      minHeight: '100px'
                                    }}>
                                      <div className="mb-2">
                                        <i className="bi bi-calendar-check" style={{ 
                                          color: COLORS.secondary,
                                          fontSize: '1.4rem'
                                        }}></i>
                                      </div>
                                      <div className="fw-bold mb-1" style={{ 
                                        color: COLORS.primary,
                                        fontSize: '1.2rem'
                                      }}>
                                        {formatDuree(abonnement.type_abonnment)}
                                      </div>
                                      <small className="text-muted" style={{ fontSize: '0.8rem' }}>
                                        Durée de l'abonnement
                                      </small>
                                    </div>
                                  </div>
                                  
                                  <div className="col-6 mb-3">
                                    <div className="text-center p-3 rounded h-100" style={{ 
                                      backgroundColor: '#E8F5E9',
                                      border: `1px solid ${COLORS.success}20`,
                                      minHeight: '100px'
                                    }}>
                                      <div className="mb-2">
                                        <i className="bi bi-cash" style={{ 
                                          color: COLORS.success,
                                          fontSize: '1.4rem'
                                        }}></i>
                                      </div>
                                      <div className="fw-bold mb-1" style={{ 
                                        color: '#2e7d32',
                                        fontSize: '1.2rem'
                                      }}>
                                        {formatPrix(prixMensuel)}
                                      </div>
                                      <small className="text-muted" style={{ fontSize: '0.8rem' }}>
                                        Coût mensuel
                                      </small>
                                    </div>
                                  </div>
                                  
                                  <div className="col-6">
                                    <div className="text-center p-3 rounded h-100" style={{ 
                                      backgroundColor: '#F5F5F5',
                                      border: `1px solid ${COLORS.darkText}20`,
                                      minHeight: '100px'
                                    }}>
                                      <div className="mb-2">
                                        <i className="bi bi-calendar-range" style={{ 
                                          color: COLORS.darkText,
                                          fontSize: '1.4rem'
                                        }}></i>
                                      </div>
                                      <div className="fw-bold mb-1" style={{ 
                                        color: COLORS.primary,
                                        fontSize: '1.2rem'
                                      }}>
                                        {formatDate(abonnement.date_debut_abonnemt)}
                                      </div>
                                      <small className="text-muted" style={{ fontSize: '0.8rem' }}>
                                        Début
                                      </small>
                                    </div>
                                  </div>
                                  
                                  <div className="col-6">
                                    <div className="text-center p-3 rounded h-100" style={{ 
                                      backgroundColor: '#FFF3E0',
                                      border: `1px solid ${COLORS.warning}20`,
                                      minHeight: '100px'
                                    }}>
                                      <div className="mb-2">
                                        <i className="bi bi-calendar-x" style={{ 
                                          color: COLORS.warning,
                                          fontSize: '1.4rem'
                                        }}></i>
                                      </div>
                                      <div className="fw-bold mb-1" style={{ 
                                        color: COLORS.warning,
                                        fontSize: '1.2rem'
                                      }}>
                                        {formatDate(abonnement.date_fin_abonnement)}
                                      </div>
                                      <small className="text-muted" style={{ fontSize: '0.8rem' }}>
                                        Date de fin
                                      </small>
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Prix total et timeline */}
                                <div className="mb-4">
                                  <div className="d-flex justify-content-between align-items-center mb-3">
                                    <span style={{ color: COLORS.darkText, fontSize: '1rem', fontWeight: '500' }}>
                                      <i className="bi bi-cash-stack me-1"></i>
                                      Montant total
                                    </span>
                                    <span className="fw-bold" style={{ 
                                      color: COLORS.primary,
                                      fontSize: '1.5rem'
                                    }}>
                                      {formatPrix(abonnement.montant_abonnement)}
                                    </span>
                                  </div>
                                  
                                  <div className="mb-2" style={{ color: COLORS.darkText, fontSize: '0.9rem' }}>
                                    <i className="bi bi-clock me-1"></i>
                                    <strong>{joursRestants > 0 ? `${joursRestants} jour${joursRestants > 1 ? 's' : ''} restant${joursRestants > 1 ? 's' : ''}` : 'Expiré'}</strong>
                                    <span className="ms-2">(Jusqu'au {formatDate(abonnement.date_fin_abonnement)})</span>
                                  </div>
                                  
                                  <div className="progress mb-1" style={{ height: '8px', borderRadius: '4px' }}>
                                    <div 
                                      className="progress-bar" 
                                      role="progressbar" 
                                      style={{ 
                                        width: joursRestants > 0 && joursRestants <= 30 
                                          ? `${Math.max(5, Math.min(100, (joursRestants / 30) * 100))}%`
                                          : joursRestants <= 0 ? '0%' : '100%',
                                        backgroundColor: statusBadge.cardHeaderColor,
                                        borderRadius: '4px'
                                      }}
                                      aria-valuenow={joursRestants} 
                                      aria-valuemin="0" 
                                      aria-valuemax="30"
                                    ></div>
                                  </div>
                                  
                                  <div className="d-flex justify-content-between mt-2">
                                    <small className="text-muted" style={{ fontSize: '0.8rem' }}>
                                      Début: {formatDate(abonnement.date_debut_abonnemt)}
                                    </small>
                                    <small className="text-muted" style={{ fontSize: '0.8rem' }}>
                                      Fin: {formatDate(abonnement.date_fin_abonnement)}
                                    </small>
                                  </div>
                                </div>
                                
                                {/* Badges de statut et type */}
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                  <div>
                                    <span className="badge fw-semibold" style={{ 
                                      backgroundColor: statusBadge.bgColor,
                                      color: statusBadge.textColor,
                                      padding: '8px 16px',
                                      borderRadius: '8px',
                                      fontSize: '0.9rem',
                                      border: `1px solid ${statusBadge.textColor}20`
                                    }}>
                                      <i className={`bi ${statusBadge.icon} me-2`}></i>
                                      {statusBadge.label}
                                    </span>
                                  </div>
                                  
                                  <div>
                                    <span className="badge fw-semibold" style={{ 
                                      backgroundColor: `${typeColor}15`,
                                      color: typeColor,
                                      padding: '6px 12px',
                                      borderRadius: '6px',
                                      fontSize: '0.85rem',
                                      border: `1px solid ${typeColor}30`
                                    }}>
                                      <i className={`bi ${getTypeIcon(abonnement.type_abonnment)} me-2`}></i>
                                      {getTypeLabel(abonnement.type_abonnment)}
                                    </span>
                                  </div>
                                </div>

                                {abonnement.mode_paiement === 'en_ligne' && abonnement.statut_paiement === 'pending' && abonnement.paiement_created_at && (
                                  <div className="mt-2" style={{ color: COLORS.warning, fontSize: '0.85rem' }}>
                                    <i className="bi bi-clock-history me-1"></i>
                                    <CompteARebours createdAt={abonnement.paiement_created_at} />
                                  </div>
                                )}
                              </div>
                              
                              {/* Pied de la carte - AGRANDI */}
                              <div className="card-footer border-0 pt-0 pb-4 px-4">
                                <div className="d-flex gap-3">
                                  <Link
                                    to={`/abonnements/details/${abonnement.id_abonnement}`}
                                    className="btn fw-semibold"
                                    style={{
                                      color: COLORS.primary,
                                      borderColor: COLORS.primary,
                                      backgroundColor: 'transparent',
                                      borderRadius: '8px',
                                      padding: '10px 20px',
                                      flex: 1,
                                      transition: 'all 0.3s ease',
                                      fontSize: '1rem'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.backgroundColor = COLORS.primary;
                                      e.target.style.color = COLORS.white;
                                      e.target.style.transform = 'translateY(-2px)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.backgroundColor = 'transparent';
                                      e.target.style.color = COLORS.primary;
                                      e.target.style.transform = 'translateY(0)';
                                    }}
                                  >
                                    <i className="bi bi-eye me-2"></i>
                                    Voir les détails
                                  </Link>
                                  
                                  {(() => {
                                    const estEssai = abonnement.mode_paiement === 'essai';

                                    // Une période d'essai ne se "renouvelle" pas, et l'action
                                    // "Souscrire un abonnement" est déjà proposée globalement
                                    // (Dashboard) — pas besoin de la dupliquer sur chaque carte essai.
                                    if (estEssai) {
                                      return null;
                                    }

                                    // Le paiement en attente bloque TOUTE souscription/renouvellement
                                    // pour la société entière (voir peut_souscrire_abonnement côté
                                    // backend) — pas seulement sur la ligne où il apparaît.
                                    const paiementEnAttenteSociete = abonnements.some(a =>
                                      a.id_societe === abonnement.id_societe &&
                                      a.mode_paiement === 'en_ligne' &&
                                      a.statut_paiement === 'pending'
                                    );

                                    if (!(currentProfile === "1" || (currentProfile === "3" && abonnement.statut_abonnement === 1))) {
                                      return null;
                                    }
                                    if (paiementEnAttenteSociete) {
                                      return null;
                                    }

                                    return (
                                      <Link
                                        to={`/abonnements/renew/${abonnement.id_abonnement}`}
                                        className="btn fw-semibold"
                                        style={{
                                          color: COLORS.white,
                                          backgroundColor: COLORS.success,
                                          borderColor: COLORS.success,
                                          borderRadius: '8px',
                                          padding: '10px 20px',
                                          flex: 1,
                                          transition: 'all 0.3s ease',
                                          fontSize: '1rem'
                                        }}
                                        onMouseEnter={(e) => {
                                          e.target.style.backgroundColor = '#2e7d32';
                                          e.target.style.borderColor = '#2e7d32';
                                          e.target.style.transform = 'translateY(-2px)';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.target.style.backgroundColor = COLORS.success;
                                          e.target.style.borderColor = COLORS.success;
                                          e.target.style.transform = 'translateY(0)';
                                        }}
                                      >
                                        <i className="bi bi-arrow-repeat me-2"></i>
                                        Renouveler
                                      </Link>
                                    );
                                  })()}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Résumé */}
                    <div className="row mt-5">
                      <div className="col-12">
                        <div className="card" style={{ 
                          backgroundColor: '#F5F7FA',
                          border: 'none',
                          borderRadius: '12px'
                        }}>
                          <div className="card-body py-3">
                            <div className="text-center">
                              <small className="text-muted">
                                <i className="bi bi-info-circle me-1"></i>
                                {abonnements.length} abonnement{abonnements.length > 1 ? 's' : ''} trouvé{abonnements.length > 1 ? 's' : ''}
                                {currentProfile === "3" && " pour votre société"}
                                <span className="ms-3">
                                  <i className="bi bi-shield-lock me-1"></i>
                                  Données sécurisées
                                </span>
                                <span className="ms-3">
                                  <i className="bi bi-calendar me-1"></i>
                                  {stats.actifs} actifs, {stats.expires} expirés, {stats.inactifs} inactifs
                                </span>
                              </small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      {/* Styles CSS additionnels */}
      <style>{`
        .pagetitle h1,
        .card-title,
        .btn,
        th {
          font-family: 'Inter', sans-serif;
        }
        
        .card-body,
        .table,
        td,
        small,
        .alert {
          font-family: 'Roboto', sans-serif;
        }
        
        .btn:focus,
        .form-control:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .btn,
        .badge,
        .card {
          transition: all 0.2s ease-in-out;
        }
        
        .btn:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        .form-control:focus {
          border-color: ${COLORS.secondary};
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .form-check-input:checked {
          background-color: ${COLORS.primary};
          border-color: ${COLORS.primary};
        }
        
        .form-check-input:focus {
          border-color: ${COLORS.secondary};
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .badge {
          font-weight: 500;
        }
        
        .bi.spin {
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        /* Amélioration de la lisibilité pour les cartes agrandies */
        .progress {
          overflow: visible;
        }
        
        .progress-bar {
          position: relative;
          overflow: visible;
        }
        
        .progress-bar::after {
          content: '';
          position: absolute;
          right: -6px;
          top: -3px;
          width: 12px;
          height: 12px;
          background-color: inherit;
          border-radius: 50%;
          border: 2px solid ${COLORS.white};
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
        
        /* Animation pour les badges */
        .badge:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0,0,0,0.1);
        }
        
        /* Pour s'assurer que les cartes ont la même hauteur */
        .card.h-100 {
          display: flex;
          flex-direction: column;
        }
        
        .card-body {
          flex: 1;
        }
        
        /* Améliorations responsive pour 2 cartes */
        @media (max-width: 1200px) {
          .col-lg-6 {
            flex: 0 0 100%;
            max-width: 100%;
          }
          
          .card {
            max-width: 600px;
            margin: 0 auto;
          }
        }
        
        @media (max-width: 768px) {
          .card {
            margin: 0 -16px;
            border-radius: 0 !important;
            max-width: 100%;
          }
          
          .pagetitle h1 {
            font-size: 1.5rem;
          }
          
          .d-flex.justify-content-between {
            flex-direction: column;
            gap: 16px;
          }
          
          .btn {
            width: 100%;
            margin-bottom: 8px;
          }
          
          .row.g-4 {
            margin: 0 -8px;
          }
          
          .col-md-6 {
            padding: 0 8px;
          }
          
          .card-body .row .col-6 {
            padding: 0 8px;
          }
          
          .d-flex.gap-2 {
            justify-content: center;
            flex-wrap: wrap;
          }
          
          .btn {
            margin-bottom: 8px;
          }
          
          .row .col-md-3, .row .col-md-4, .row .col-md-2 {
            margin-bottom: 16px;
          }
          
          .card-header h5 {
            font-size: 1.1rem;
          }
          
          .card-body .fw-bold {
            font-size: 1rem;
          }
          
          .d-flex.gap-3 {
            flex-direction: column;
            gap: 10px !important;
          }
        }
        
        @media (max-width: 576px) {
          .col-md-6 {
            flex: 0 0 100%;
            max-width: 100%;
          }
          
          .card-header h5 {
            font-size: 1rem;
          }
          
          .card-body .fw-bold {
            font-size: 0.9rem;
          }
          
          .card-body .row .col-6 {
            flex: 0 0 50%;
            max-width: 50%;
          }
        }
      `}</style>
    </Layout>
  );
};

export default AbonnementsList;